//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React, { HTMLAttributes } from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { isDefined } from '@utils/filterPredicates';

interface Props extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  iconName?: UnisonIconType;
  imageUrl?: string;
  initials?: string;
  username?: string;
  size?: number;
}

/**
 * The Avatar atom renders a circle with an image (if one is given), 1-2 initials (depending
 * on whether a username or initials is given), icon name, or a user icon (if nothing is given or the
 * imageUrl fails to load). The Avatar defaults to 32x32px but is fully responsive and can
 * be resized as needed with the `size` prop.
 */
const Avatar = ({
  className = '',
  iconName,
  imageUrl,
  initials,
  size = 32,
  username,
}: Props) => {
  const [hasValidImageUrl, setIsImageUrlValid] = React.useState(true);
  const placeholderSize = size / 3;

  const getLetters = () => {
    if (username) {
      return username.slice(0, 1);
    } else if (initials) {
      // We show only the first two initials to help with resizing
      return initials.slice(0, 2);
    } else {
      return undefined;
    }
  };

  const getAvatarContent = () => {
    const letters = getLetters();

    if (imageUrl && hasValidImageUrl) {
      return (
        <AvatarImage onError={() => setIsImageUrlValid(false)} src={imageUrl} />
      );
    } else if (letters) {
      return (
        <AvatarInitials numLetters={letters.length} size={placeholderSize}>
          {letters}
        </AvatarInitials>
      );
    } else if (isDefined(iconName)) {
      return <Icon name={iconName} />;
    } else {
      return <AvatarIcon size={placeholderSize} />;
    }
  };

  return (
    <AvatarWrapper className={className} size={size}>
      {getAvatarContent()}
    </AvatarWrapper>
  );
};

const AvatarWrapper = styled.div<{ size: number }>(
  ({ size, theme }) => `
  display: inline-flex;
  background-color: ${theme.colors.gray2};
  height: ${size}px;
  width: ${size}px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${theme.colors.aliases.bodySecondary};
`,
);

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarInitials = styled.div<{
  size: number;
  numLetters: number;
}>(
  ({ numLetters, size, theme }) => `
  font-weight: ${theme.fontWeights.bold};
  font-size: ${size}px;
  text-transform: uppercase;

  &::first-letter {
    letter-spacing: ${numLetters === 1 ? 0 : 2}px;
  }
`,
);

const AvatarIcon = styled(Icon).attrs({
  name: 'user',
})<{ size: number }>(
  ({ size }) => `
  width: ${size}px;
  height: ${size}px;
`,
);

export default Avatar;
